import { mixed, object, string } from 'yup';

import { TEXT_AREA_MAX_LENGTH } from '../../../../../common';
import {
  MEDICAL_DIAGNOSIS_YEAR_BOUNDS,
  MEDICAL_NUMERIC_FIELD_BOUNDS,
  MEDICAL_TEXT_MIN_LENGTH,
} from './constants';

import type { Moment } from 'moment';
import type { NumericFieldBounds } from './types';

const requiredInteger = ({ min, max }: NumericFieldBounds) =>
  string()
    .trim()
    .matches(/^\d+$/, 'Enter a whole number')
    .test('within-range', `Enter a number between ${min} and ${max}`, (value) => {
      const parsed = Number(value);
      return !Number.isInteger(parsed) || (parsed >= min && parsed <= max);
    })
    .required();

const screeningDateSchema = (areScreeningDatesRequired: boolean) =>
  areScreeningDatesRequired ? mixed<Moment>().nullable().required() : mixed<Moment>().nullable();

export const getMedicalInfoSchema = (areScreeningDatesRequired: boolean) =>
  object({
    allergies: string().trim().required(),
    special_diets: string().trim().required(),
    hearing_difficulty: string().required(),
    hearing_screening_date: screeningDateSchema(areScreeningDatesRequired),
    vision_difficulty: string().required(),
    vision_screening_date: screeningDateSchema(areScreeningDatesRequired),
    trauma_history: string()
      .trim()
      .max(
        TEXT_AREA_MAX_LENGTH,
        `Trauma history must be at most ${TEXT_AREA_MAX_LENGTH} characters`,
      )
      .required(),
    hospitalization_history: string()
      .trim()
      .max(
        TEXT_AREA_MAX_LENGTH,
        `Surgical or hospitalization history must be at most ${TEXT_AREA_MAX_LENGTH} characters`,
      )
      .required(),
    f84_severity_level: string().required(),
    f84_diagnosis_provider_name: string()
      .trim()
      .min(
        MEDICAL_TEXT_MIN_LENGTH,
        `Provider name must be at least ${MEDICAL_TEXT_MIN_LENGTH} characters`,
      )
      .required(),
    f84_diagnosis_city: string().trim().required(),
    f84_diagnosis_state: string().required(),
    f84_diagnosis_year: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.f84_diagnosis_year),
    child_delivery_type: string().required(),
    child_gestational_age: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.child_gestational_age),
    child_weight_at_birth_pounds: requiredInteger(
      MEDICAL_NUMERIC_FIELD_BOUNDS.child_weight_at_birth_pounds,
    ),
    child_weight_at_birth_ounces: requiredInteger(
      MEDICAL_NUMERIC_FIELD_BOUNDS.child_weight_at_birth_ounces,
    ),
    child_sitting_up_age_months: requiredInteger(
      MEDICAL_NUMERIC_FIELD_BOUNDS.child_sitting_up_age_months,
    ),
    child_crawling_age_months: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.child_crawling_age_months),
    child_walking_age_years: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.child_walking_age_years),
    child_walking_age_months: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.child_walking_age_months),
    child_standing_age_years: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.child_standing_age_years),
    child_standing_age_months: requiredInteger(
      MEDICAL_NUMERIC_FIELD_BOUNDS.child_standing_age_months,
    ),
    child_babbling_age_months: requiredInteger(MEDICAL_NUMERIC_FIELD_BOUNDS.child_babbling_age_months),
    child_first_word: string().trim().required(),
    child_second_word: string().trim().required(),
    child_first_words_age_months: requiredInteger(
      MEDICAL_NUMERIC_FIELD_BOUNDS.child_first_words_age_months,
    ),
  });

export const MEDICAL_DIAGNOSIS_SCHEMA = object({
  diagnosis_name: string().required(),
  diagnosis_provider_name: string()
    .trim()
    .min(
      MEDICAL_TEXT_MIN_LENGTH,
      `Provider name must be at least ${MEDICAL_TEXT_MIN_LENGTH} characters`,
    )
    .required(),
  diagnosis_city: string().trim().required(),
  diagnosis_state: string().required(),
  diagnosis_year: requiredInteger(MEDICAL_DIAGNOSIS_YEAR_BOUNDS),
});
